/**
 * Every settings-table key by name, read by the API that stores them and by
 * any client-facing screen that needs to name one -- the admin settings page
 * used to retype a handful of these (`'smtp.host'`, `'dmca.email'`) as bare
 * string literals in its own `SERVICE_KEYS` table, which a rename here would
 * have silently stopped matching.
 *
 * Lives in `@content-removal/shared` rather than `@content-removal/runtime`
 * for exactly that reason: this is plain data, no Prisma or Node dependency
 * behind it, and `runtime` pulls in both -- importing it from a `'use
 * client'` page would ship a database client into the browser bundle.
 * `packages/runtime`'s own `setting-keys.ts` re-exports this so every
 * existing `import { SettingKey } from '@content-removal/runtime'` on the
 * API side keeps working unchanged.
 */
export const SettingKey = {
  // Stripe credentials, the live source of truth in the DB like every other
  // provider key (env is only the first-run fallback). The secret key signs
  // every API call; the webhook secret verifies Stripe's callbacks.
  STRIPE_SECRET_KEY: 'stripe.secret_key',
  STRIPE_WEBHOOK_SECRET: 'stripe.webhook_secret',
  // Blank means the list price in PLAN_LIST_PRICE, never a plan nobody can buy.
  PRICE_ESSENTIAL_USD: 'price.essential_usd',
  PRICE_PRO_USD: 'price.pro_usd',
  PRICE_PREMIUM_USD: 'price.premium_usd',
  // Annual list price for the three creator plans, shown on the public site's
  // Monthly/Annual toggle. Blank means the annual list price in PLAN_LIST_PRICE.
  PRICE_ESSENTIAL_ANNUAL_USD: 'price.essential_annual_usd',
  PRICE_PRO_ANNUAL_USD: 'price.pro_annual_usd',
  PRICE_PREMIUM_ANNUAL_USD: 'price.premium_annual_usd',
  PRICE_AGENCY_STARTER_USD: 'price.agency_starter_usd',
  PRICE_AGENCY_STARTER_PROFILE_USD: 'price.agency_starter_profile_usd',
  PRICE_AGENCY_GROWTH_USD: 'price.agency_growth_usd',
  PRICE_AGENCY_GROWTH_PROFILE_USD: 'price.agency_growth_profile_usd',
  PRICE_AGENCY_SCALE_USD: 'price.agency_scale_usd',
  PRICE_AGENCY_SCALE_PROFILE_USD: 'price.agency_scale_profile_usd',
  PRICE_EXTRA_USERNAME_USD: 'price.extra_username_usd',
  // What each creator plan includes. Blank means the figure in PLAN_ENTITLEMENTS.
  PLAN_ESSENTIAL_USERNAMES: 'plan.essential_usernames',
  PLAN_PRO_USERNAMES: 'plan.pro_usernames',
  PLAN_PREMIUM_USERNAMES: 'plan.premium_usernames',
  // The rows on each plan's public card, one `Label: value` per line. Blank means the built-in card.
  PLAN_ESSENTIAL_CARD: 'plan.essential_card',
  PLAN_PRO_CARD: 'plan.pro_card',
  PLAN_PREMIUM_CARD: 'plan.premium_card',
  PLAN_ESSENTIAL_PROFILES: 'plan.essential_profiles',
  PLAN_PRO_PROFILES: 'plan.pro_profiles',
  PLAN_PREMIUM_PROFILES: 'plan.premium_profiles',
  // Public product claims shown across the marketing site, editable without a
  // deploy so Content/SEO own the wording. Default is DEFAULT_LEAK_SITES_COUNT.
  PUBLIC_LEAK_SITES_COUNT: 'product.leak_sites_count',
  // Complexity points one filed removal costs, by where the page is.
  POINTS_WEIGHTS: 'points.weights',
  // How often a paid profile is searched again, in days: one number for both
  // halves of a scan, because the client is promised one search.
  SEARCH_EVERY_DAYS: 'search.every_days',
  // Below this many days since the last scan, a repeat search asks the
  // provider for the last month only (`&tbs=qdr:m`). Older than it, the whole
  // index again: a gap longer than the window is exactly what the filter would
  // hide.
  SEARCH_RECENT_WINDOW_DAYS: 'search.recent_window_days',
  /* The paid WHOIS API, the only automatic source of a contact (DEC-034),
     asked once per domain. 150k lookups a month for $4.99, stopped by the
     service itself when spent. No key means nothing is looked up. */
  /* The switch, separate from the key: turning the paid lookup off for an
     afternoon must not mean deleting a key somebody then has to find again. */
  WHOIS_APILAYER_ENABLED: 'whois.apilayer.enabled',
  WHOIS_APILAYER_KEY: 'whois.apilayer.key',
  /* The one-off run of the whole catalogue through the paid WHOIS (DEC-035),
     and how many lookups it may spend before it stops by itself. */
  WHOIS_PREFILL_ENABLED: 'whois.prefill.enabled',
  WHOIS_PREFILL_LIMIT: 'whois.prefill.limit',
  /* Not on the panel: 'outage' while an outage, not a person, has the run
     switched off. Kept in the database, since a note in Redis was lost overnight. */
  WHOIS_PREFILL_RESUME: 'whois.prefill.resume',
  /* Not on the panel: 'yes' while the RDAP pass has a list to finish, so a deploy
     does not end it when the paid run has already switched itself off. */
  WHOIS_RDAP_PENDING: 'whois.rdap.pending',
  DATAFORSEO_LOGIN: 'dataforseo.login',
  DATAFORSEO_PASSWORD: 'dataforseo.password',
  // Named for what it limits rather than for who is billed. The ceiling is on
  // paid search, and it would outlive a change of provider.
  SEARCH_DAILY_BUDGET_USD: 'search.daily_budget_usd',
  // A second, narrower ceiling: the total above catches a runaway across
  // every account combined, this catches one account alone running away
  // while the rest of the day looks normal.
  SEARCH_ACCOUNT_DAILY_CEILING_USD: 'search.account_daily_ceiling_usd',
  SEARCH_IMAGE_ENABLED: 'search.image_enabled',
  // How often a quiet profile is worth the site-scoped chains, which are five
  // times the price each and about 94% of what a sweep costs.
  SEARCH_CHAIN_EVERY_DAYS: 'search.chain_every_days',
  // How many results deep a search goes. Two tiers plus a window that switches
  // between them: full depth on a profile's first scan and shortly before a
  // paying account renews, shallow every other day.
  SEARCH_DEPTH_FIRST_SCAN: 'search.depth_first_scan',
  SEARCH_DEPTH_STEADY: 'search.depth_steady',
  SEARCH_DEPTH_RENEWAL_WINDOW_DAYS: 'search.depth_renewal_window_days',
  // How many days either side of the window above a given account's own
  // renewal-window length actually lands on, so a client never sees the
  // deep tier start on the same day of their cycle every single time.
  SEARCH_DEPTH_RENEWAL_JITTER_DAYS: 'search.depth_renewal_jitter_days',
  SEARCH_SANDBOX: 'search.sandbox',
  SEARCH_PROBE_ENABLED: 'search.probe_enabled',
  CRAWL_CATALOGUE_ENABLED: 'crawl.catalogue_enabled',
  // Refuses to run a comparison whose generator produced more queries than
  // this — a manager selecting several strategies plus every test site can
  // otherwise generate thousands of real DataForSEO requests by accident.
  SEARCH_COMPARE_MAX_QUERIES: 'search_compare.max_queries',
  PUBLIC_SCAN_DAILY_SEARCHES: 'public_scan.daily_searches',
  // DMCA endpoint (DMCA_API_BASE) is infra, from .env only, not a DB row.
  DMCA_EMAIL: 'dmca.email',
  DMCA_PASSWORD: 'dmca.password',
  /** Free MaxMind key: with it, the API downloads the GeoLite2 city database
   *  itself and refreshes it monthly; without it the panel shows the address
   *  alone. Nothing leaves this machine either way — the lookup is a file
   *  read. */
  GEOIP_LICENSE_KEY: 'geoip.license_key',
  SMTP_HOST: 'smtp.host',
  SMTP_PORT: 'smtp.port',
  SMTP_USER: 'smtp.user',
  SMTP_PASSWORD: 'smtp.password',
  /**
   * Mail to clients goes out through its own relay (Mandrill), so the abuse and
   * legal letters, which fall back to `smtp.*`, stay on the mailbox they reply
   * to. Every row falls back to `smtp.*` when empty; `from` falls back to
   * `smtp.client.user`, which is only an address while the relay is a mailbox.
   */
  SMTP_CLIENT_HOST: 'smtp.client.host',
  SMTP_CLIENT_PORT: 'smtp.client.port',
  SMTP_CLIENT_USER: 'smtp.client.user',
  SMTP_CLIENT_PASSWORD: 'smtp.client.password',
  SMTP_CLIENT_FROM: 'smtp.client.from',
  SMTP_CLIENT_REPLY_TO: 'smtp.client.reply_to',
  SMTP_CLIENT_SUPPORT_FROM: 'smtp.client.support_from',
  MAILCHIMP_API_KEY: 'mailchimp.api_key',
  /** The `usNN` after the key's dash. Empty reads it from the key. */
  MAILCHIMP_SERVER: 'mailchimp.server',
  MAILCHIMP_AUDIENCE_ID: 'mailchimp.audience_id',
  /** Cloudflare Turnstile for the public contact form. Site key is public (sent
   *  to the browser via /public/claims); secret stays server-side. Empty secret
   *  turns the check off, so dev/local work with no keys. */
  TURNSTILE_SITE_KEY: 'turnstile.site_key',
  TURNSTILE_SECRET: 'turnstile.secret',
  // The same sandbox idea as `search.sandbox`, for the other channel that can
  // reach a real stranger: on, every notice this account would send goes to
  // a local Mailpit inbox instead of `smtp.host`, so a dev/test environment
  // can exercise the real send code without a real abuse desk ever seeing
  // one. The real host/user/password above are left untouched either way —
  // turning this off is the only thing that makes them live again.
  SMTP_SANDBOX: 'smtp.sandbox',
  /**
   * Three mailboxes, one relay each, because three kinds of letter should not
   * come from one address.
   *
   * A client writing back to the address that told her a case had moved would
   * otherwise land in the same inbox as a hosting company's abuse desk, and a
   * delisting request signed from `support@` reads to Google like a fan
   * complaint rather than a rights holder's filing. So: `smtp.*` above is the
   * one support and the client screens use, `smtp.abuse.*` writes to hosts,
   * registrars and CDNs, `smtp.legal.*` files delisting and DMCA.
   *
   * Every one of them falls back to `smtp.*` when left empty, which is what
   * makes this additive: an environment with one mailbox keeps working
   * exactly as it did, and filling a row in is what splits that traffic off.
   */
  SMTP_ABUSE_HOST: 'smtp.abuse.host',
  SMTP_ABUSE_PORT: 'smtp.abuse.port',
  SMTP_ABUSE_USER: 'smtp.abuse.user',
  SMTP_ABUSE_PASSWORD: 'smtp.abuse.password',
  SMTP_LEGAL_HOST: 'smtp.legal.host',
  SMTP_LEGAL_PORT: 'smtp.legal.port',
  SMTP_LEGAL_USER: 'smtp.legal.user',
  SMTP_LEGAL_PASSWORD: 'smtp.legal.password',
  /**
   * How many minutes a stand-in may hold a covered client open, doing
   * nothing, before it goes back to whoever it belongs to.
   *
   * A number, in the panel, because the right one depends on how the team
   * actually works: too short and somebody loses a client while reading a
   * long reply from a host, too long and the regular member sits idle after
   * coming back. Only ever applies once the regular member is active again —
   * nothing takes a client off somebody who is the only one holding it.
   */
  SUPPORT_HANDBACK_IDLE_MINUTES: 'support.handback_idle_minutes',
  /** From how many models an agency's cases are shared out across the team
   *  instead of being worked by whoever owns each model. The account's own
   *  switch, when a supervisor has set one, always wins over this number. */
  SUPPORT_SPREAD_FROM_MODELS: 'support.spread_from_models',
  // S3/storage is configured from .env only (StorageService), never a DB row.
  // Slack is managed from /admin/settings like every other integration: a
  // webhook per channel, a bot token and channel for two-way support, and the
  // signing secret for inbound events. Read from the database and nowhere else.
  SLACK_WEBHOOK_OPS: 'slack.webhook_ops',
  SLACK_WEBHOOK_ERRORS_BACK: 'slack.webhook_errors_back',
  SLACK_WEBHOOK_ERRORS_FRONT: 'slack.webhook_errors_front',
  SLACK_BOT_TOKEN: 'slack.bot_token',
  SLACK_CHANNEL_OPS: 'slack.channel_ops',
  SLACK_SIGNING_SECRET: 'slack.signing_secret',
  // The payments funnel channel: one Slack thread per customer, posted by the
  // bot. A channel id, not a webhook, because threading needs chat.postMessage.
  SLACK_CHANNEL_PAYMENTS: 'slack.channel_payments',
  // The watch feed: what the addresses on the Audit log's watch list do, one thread each.
  SLACK_CHANNEL_WATCH: 'slack.channel_watch',
  // Addresses whose every action is posted to Slack, one per line. Kept from the Audit log screen.
  AUDIT_WATCH_EMAILS: 'audit.watch_emails',
  WHATSAPP_TOKEN: 'whatsapp.token',
  WHATSAPP_PHONE_NUMBER_ID: 'whatsapp.phone_number_id',
  WHATSAPP_VERIFY_TOKEN: 'whatsapp.verify_token',
  // The public number paid clients open a chat with from Help.
  WHATSAPP_SUPPORT_NUMBER: 'whatsapp.support_number',
  // Meta's App Secret, HMACs the raw body of every inbound webhook POST.
  // Different from WHATSAPP_TOKEN (that authenticates our outbound calls to
  // Meta; this authenticates Meta's inbound calls to us) and from
  // WHATSAPP_VERIFY_TOKEN (that is only ever used once, at setup, over GET).
  WHATSAPP_APP_SECRET: 'whatsapp.app_secret',
  CLOUDFLARE_API_TOKEN: 'cloudflare.api_token',
  CLOUDFLARE_ACCOUNT_ID: 'cloudflare.account_id',
  // The zone whose cache the Actions page purges; a purge token needs the
  // Cache Purge permission, which the abuse token above does not carry.
  CLOUDFLARE_ZONE_ID: 'cloudflare.zone_id',
  CLOUDFLARE_PURGE_TOKEN: 'cloudflare.purge_token',
  ABUSE_REPLY_TO: 'abuse.reply_to',
  // The company's own particulars. One place, because the same six facts are
  // printed on the terms, the privacy notice, every takedown notice we send and
  // the account the notices are filed from. Anywhere they are typed twice they
  // will eventually disagree, and a notice naming a company that does not match
  // the one on the website is a notice worth arguing with.
  COMPANY_LEGAL_NAME: 'company.legal_name',
  COMPANY_REGISTRATION_NUMBER: 'company.registration_number',
  COMPANY_ADDRESS: 'company.address',
  COMPANY_JURISDICTION: 'company.jurisdiction',
  COMPANY_SUPERVISORY_AUTHORITY: 'company.supervisory_authority',
  COMPANY_DPO_CONTACT: 'company.dpo_contact',
  // The contact line the public site shows (footer, contact page). Editable in
  // the panel so a changed number or inbox does not need a deploy. The email
  // still has its own baked-in constant (contact.ts) for structured data and
  // legal pages; this is the display value, which falls back to that constant.
  COMPANY_SUPPORT_PHONE: 'company.support_phone',
  COMPANY_SUPPORT_EMAIL: 'company.support_email',
  LEGAL_EVIDENCE_RETENTION_YEARS: 'legal.evidence_retention_years',
  LEGAL_BILLING_RETENTION_YEARS: 'legal.billing_retention_years',
  LEGAL_WITHDRAWAL_RIGHT: 'legal.withdrawal_right',
  PRICING_VAT: 'pricing.vat',
  // The brand as shown to visitors (header, footer, schema, page titles) and the
  // public particulars that were hardcoded across the marketing pages until now,
  // moved here so the site and the legal docs cannot drift from one another.
  COMPANY_BRAND_NAME: 'company.brand_name',
  COMPANY_FOUNDED_YEAR: 'company.founded_year',
  COMPANY_DOMAIN: 'company.domain',
  COMPANY_SOCIAL_INSTAGRAM: 'company.social_instagram',
  COMPANY_SOCIAL_TELEGRAM: 'company.social_telegram',
  COMPANY_SOCIAL_REDDIT: 'company.social_reddit',
  // The "last updated" stamp on each legal document, and the durations the Terms
  // quote, so a clause and its number are one edit, not a code change.
  LEGAL_TERMS_LAST_UPDATED: 'legal.terms_last_updated',
  LEGAL_PRIVACY_LAST_UPDATED: 'legal.privacy_last_updated',
  LEGAL_WITHDRAWAL_DAYS: 'legal.withdrawal_days',
  LEGAL_DISPUTE_RESPONSE_DAYS: 'legal.dispute_response_days',
  LEGAL_DISPUTE_ESCALATION_DAYS: 'legal.dispute_escalation_days',
  LEGAL_MIN_AGE: 'legal.min_age',
  // Visitor-facing team numbers on the About page, owned by Content.
  PUBLIC_TEAM_SIZE: 'product.team_size',
  PUBLIC_COUNTRIES_COUNT: 'product.countries_count',
  PUBLIC_SPECIALISTS_COUNT: 'product.specialists_count',
  TAKEDOWN_REQUIRE_IMAGE_MATCH: 'takedown.require_image_match',
  // Off means exactly what it says today: nothing is sent without a person
  // clicking Approve in /queue first, which is the only path into dispatch()
  // that exists right now. There is no automatic approval to gate; this is
  // the switch a future one would check, shown off rather than left unsaid.
  TAKEDOWN_AUTO_SEND_ENABLED: 'takedown.auto_send_enabled',
  // Off means the follow-up worker still notices a rung has gone quiet and
  // still alerts support, but stops there: it does not pick the next channel
  // or queue the next case by itself. Early rollout, while support wants to
  // walk the ladder by hand and watch how the automatic pick behaves before
  // trusting it unattended.
  TAKEDOWN_AUTO_ESCALATE_ENABLED: 'takedown.auto_escalate_enabled',
  // Off means a page that now answers as gone only alerts support; the case
  // stays open until a person confirms it in the queue, rather than the
  // follow-up worker marking it REMOVED/DELISTED (and the client being told
  // so) off one automated fetch.
  TAKEDOWN_AUTO_RESOLVE_ENABLED: 'takedown.auto_resolve_enabled',
  // On by default, unlike the two above: this is the nightly erasure a client
  // was already promised in the privacy notice, not a new automation being
  // rolled out cautiously. The switch exists as an emergency brake distinct
  // from the retention-period length itself, which is a legal number, not an
  // operational pause.
  RETENTION_SWEEP_ENABLED: 'retention.sweep_enabled',
  // On by default; an incident lever for a bad template or wrong counts going
  // to every open account at once, not a rollout gate. Covers all four
  // NotificationSweepsService sweeps (findings, ended cases, weekly digest,
  // quota warnings) with one switch.
  NOTIFICATION_SWEEPS_ENABLED: 'notifications.sweeps_enabled',
  // "Continue with Google" on sign-in/sign-up. Empty in every environment
  // until a manager pastes a real pair in here — no code change, no deploy,
  // no env file, the same way DataForSEO or SMTP come alive.
  GOOGLE_CLIENT_ID: 'google.client_id',
  GOOGLE_CLIENT_SECRET: 'google.client_secret',
  // The referral programme. Off by default: switching it on is what makes the
  // invite page appear for clients and the welcome discount apply at checkout.
  REFERRALS_ENABLED: 'referrals.enabled',
  REFERRAL_REWARD_PERCENT: 'referrals.reward_percent',
  REFERRAL_REWARD_MONTHS: 'referrals.reward_months',
  REFERRAL_WELCOME_PERCENT: 'referrals.welcome_percent',
  REFERRAL_HOLD_DAYS: 'referrals.hold_days',
} as const;

export type SettingKeyValue = (typeof SettingKey)[keyof typeof SettingKey];
